import React from 'react';
import {useRecoilQuery} from 'recoil-toolkit';

import {FinishWithoutCompleteFlowEnum} from 'main/analytics/gaTagsEnum';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {getPageHeaderTitle, getUfPath} from 'presentation/utils';

import {APIError} from 'data/errors';
import {HttpStatusCode} from 'data/protocols/http';
import {TypeLocalNameEnum} from 'domain/usecases';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {getSpecialtyBySearch} from 'presentation/utils/GetSpecialtyBySearch';
import {useNavigate} from 'react-router-dom';
import {
	BookingErrorState,
	BookingLoadingState,
	BookingNotFoundState,
	SpecialtyGrid,
} from '../../components';
import {BookingNotFoundStateVariantEnum} from '../../components/BookingNotFoundState/types';
import {FlowEnum, PATH_TITLE} from '../../constants';

import {bookingSpecialtiesState} from './atoms';
import {SpecialtySelectionProps} from './types';
import {SpecialtyOrExamNotFound} from 'presentation/modules/booking/shared/ui/components/SpecialtyOrExamNotFound';
import {FeaturesEnum} from 'presentation/constants';
import {ExamEmptyState} from 'presentation/modules/booking/shared/ui/components/ExamEmptyState';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {textHandle} from 'presentation/utils/textHandle';

const {booking} = translations['pt-br'].others.sidebar;
const {loadingError, loadingExamsError, loadingState, loadingExamsState} =
	translations['pt-br'].privateFlow.bookingFlow.specialtyFlow;

export function SpecialtySelection({
	flowType,
	handleNextStep,
	handlePrevStep,
}: SpecialtySelectionProps) {
	const navigate = useNavigate();
	const resetBookingData = useBookingStore(state => state.resetBookingData);
	const bookingData = useBookingStore(state => state.bookingData);
	const {SEARCH_PARAMS, clearSearch} = useQueryParams();
	const {query, searchByLetter} = SEARCH_PARAMS;
	const isUsingGeolocation = !bookingData?.isManualLocation;
	const bookingUfPath = getUfPath(flowType);

	const {isEnabled: isExamsSpecialtyProcedureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_EXAMS_SPECIALTY_PROCEDURE,
	);
	const {isEnabled: isSemanticSearchEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SEMANTIC_SEARCH_MEDICAL_GUIDE,
	);

	const isExam = flowType === FlowEnum.EXAM;
	const isHealth = flowType === FlowEnum.HEALTH;
	const isOdonto = flowType === FlowEnum.ODONTO;

	const {
		refresh,
		loading,
		data: specialtiesList,
		error,
	} = useRecoilQuery(
		bookingSpecialtiesState({
			flowType,
			cityName:
				bookingData.isManualLocation || isExam
					? bookingData.cityName?.toUpperCase()
					: bookingData.coordinates,
			ufCode: isUsingGeolocation && isHealth ? '' : bookingData.ufCode?.toUpperCase(),
			typeLocalName:
				isUsingGeolocation && isExam ? TypeLocalNameEnum.CIDADE : bookingData.typeLocalName,
		}),
	);

	const pageTitle = getPageHeaderTitle({internalPaths: PATH_TITLE, mainPath: booking});
	const specialtiesListHasUnprocessableError =
		error && error instanceof APIError && error.statusCode === HttpStatusCode.unprocessable;

	if (error && !specialtiesListHasUnprocessableError) {
		return (
			<BookingErrorState
				showHeader={false}
				pageTitle={pageTitle}
				errorTitle={isExam ? loadingExamsError : loadingError}
				handleBack={handlePrevStep}
				handleRefresh={refresh}
			/>
		);
	}

	if (loading) {
		return (
			<BookingLoadingState
				showHeader={false}
				pageTitle={pageTitle}
				loadingText={isExam ? loadingExamsState : loadingState}
				handleBack={handlePrevStep}
			/>
		);
	}

	const NOT_FOUND =
		!specialtiesList || specialtiesList.length === 0 || specialtiesListHasUnprocessableError;

	const handleNavToStateSelection = () => {
		clearSearch();
		resetBookingData();
		navigate(bookingUfPath);
	};

	const showSpecialtyNotFound = isExam || isHealth || isOdonto;
	const showExamNotFound =
		isExam && isExamsSpecialtyProcedureFlagEnabled && isSemanticSearchEnabled;

	if (NOT_FOUND) {
		if (showExamNotFound) {
			return (
				<ExamEmptyState
					dataTagId={FinishWithoutCompleteFlowEnum.SEM_ESPECIALIDADE_ENCONTRADA}
				/>
			);
		}

		return showSpecialtyNotFound ? (
			<SpecialtyOrExamNotFound
				handlePrimaryButton={handleNavToStateSelection}
				flowType={flowType}
			/>
		) : (
			<BookingNotFoundState
				flowType={flowType}
				pageTitle={pageTitle}
				handleBack={handlePrevStep}
				variant={BookingNotFoundStateVariantEnum.SPECIALTY}
				showHeader={false}
				dataTagId={FinishWithoutCompleteFlowEnum.SEM_ESPECIALIDADE_DISPONIVEL}
				handleNext={handleNextStep}
			/>
		);
	}
	const searchTerm = textHandle(query).sanitizeText();
	const searchFilteredByTerms = getSpecialtyBySearch({text: searchTerm, flowType});

	const FILTERED_LIST_BY_CATEGORY = specialtiesList.map(specialty => ({
		...specialty,
		list: specialty?.list?.filter(item => {
			const formattedText = textHandle(item?.formattedText).sanitizeText();
			const termsRegex = new RegExp(`\\b${searchFilteredByTerms}\\b`, 'i');

			return formattedText.includes(searchTerm) || termsRegex.test(formattedText);
		}),
	}));

	const FILTERED_LIST_BY_LETTER = FILTERED_LIST_BY_CATEGORY.filter(
		item => item?.category === searchByLetter,
	);

	const LIST = searchByLetter ? FILTERED_LIST_BY_LETTER : FILTERED_LIST_BY_CATEGORY;

	return (
		<SpecialtyGrid
			filteredList={LIST}
			handleNext={handleNextStep}
			flowType={flowType}
			allSpecialtiesAvailable={specialtiesList}
		/>
	);
}
